export const PLAYER_ROLES = ["Top", "Jungle", "Mid", "ADC", "Support", "Fill"] as const;

export type PlayerRole = (typeof PLAYER_ROLES)[number];

export const ROLE_SHORT_LABEL: Record<PlayerRole, string> = {
  Top: "TOP",
  Jungle: "JGL",
  Mid: "MID",
  ADC: "ADC",
  Support: "SUP",
  Fill: "FILL",
};

const COMMUNITY_DRAGON_POSITION_BASE =
  "https://raw.communitydragon.org/latest/plugins/rcp-fe-lol-clash/global/default/assets/images/position-selector/positions";

export const ROLE_ICON_URL: Record<PlayerRole, string> = {
  Top: `${COMMUNITY_DRAGON_POSITION_BASE}/icon-position-top.png`,
  Jungle: `${COMMUNITY_DRAGON_POSITION_BASE}/icon-position-jungle.png`,
  Mid: `${COMMUNITY_DRAGON_POSITION_BASE}/icon-position-middle.png`,
  ADC: `${COMMUNITY_DRAGON_POSITION_BASE}/icon-position-bottom.png`,
  Support: `${COMMUNITY_DRAGON_POSITION_BASE}/icon-position-utility.png`,
  Fill: `${COMMUNITY_DRAGON_POSITION_BASE}/icon-position-fill.png`,
};

export function normalizeRole(value: string | null | undefined): PlayerRole | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  const segments = trimmed
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  // Sheet roles often use Lane/Sub (e.g. "Bot/Sub"). Treat any Sub/Fill segment as Fill
  // so fill players remain filterable and visible under the Fill role.
  for (const segment of segments) {
    const upper = segment.toUpperCase();
    if (upper === "SUB" || upper === "FILL") {
      return "Fill";
    }
  }

  const primary = segments[0];
  const upper = primary.toUpperCase();

  const aliases: Record<string, PlayerRole> = {
    TOP: "Top",
    JUNGLE: "Jungle",
    JGL: "Jungle",
    JG: "Jungle",
    MID: "Mid",
    MIDDLE: "Mid",
    ADC: "ADC",
    BOT: "ADC",
    BOTTOM: "ADC",
    SUPPORT: "Support",
    SUP: "Support",
    UTILITY: "Support",
  };
  if (upper in aliases) {
    return aliases[upper];
  }

  const match = PLAYER_ROLES.find((role) => role.toLowerCase() === primary.toLowerCase());
  return match ?? null;
}

export function roleIconUrl(role: PlayerRole): string {
  return ROLE_ICON_URL[role];
}

export function isPlayerRole(value: string): value is PlayerRole {
  return (PLAYER_ROLES as readonly string[]).includes(value);
}

export function roleSortIndex(role: PlayerRole | null | undefined): number {
  if (!role) {
    return PLAYER_ROLES.length;
  }

  const index = PLAYER_ROLES.indexOf(role);
  return index === -1 ? PLAYER_ROLES.length : index;
}

export function sortPlayersByRole<T extends { role: PlayerRole | null | undefined }>(
  players: T[],
): T[] {
  return [...players].sort((a, b) => roleSortIndex(a.role) - roleSortIndex(b.role));
}
